import { Icon } from "@iconify/react";
import Classnames from "classnames";
import React, { useRef } from "react";

import useHtmlProps from "@hooks/use-html-props";

import style from "./checkbox.styl";

type CheckboxProps = {
  children?: React.ReactNode;
  className?: string;
  disabled?: boolean;
  readOnly?: boolean;
  /**
   * Switch label and toggle left/right
   */
  reverse?: boolean;
  onChange?: (checked: boolean) => void;
  value?: true | false | "partial";
  tabIndex?: number;
  "data-testid"?: string;
};

const Checkbox = React.forwardRef<HTMLLabelElement, CheckboxProps>(
  (
    {
      children,
      className,
      disabled,
      readOnly,
      reverse,
      onChange,
      value,
      tabIndex,
      "data-testid": dataTestId,
      ...props
    },
    ref,
  ) => {
    const htmlProps = useHtmlProps(props);

    let internalRef = useRef<HTMLLabelElement>(null);
    if (ref) {
      internalRef = ref as React.MutableRefObject<HTMLLabelElement>;
    }

    const classNameMerged = Classnames(
      style.checkbox,
      {
        [style.disabled]: disabled,
        [style.reverse]: reverse,
        [style.readonly]: readOnly,
        [style.partial]: value === "partial",
      },
      className,
    );

    const elementStyle = {};

    const onKeyPress = (event: React.KeyboardEvent<HTMLLabelElement>) => {
      const code = event.charCode || event.keyCode;
      if (code === 32 || code === 13) {
        event.preventDefault();
        (window.document.activeElement as HTMLElement)?.click();
      }
    };

    return (
      <label
        className={classNameMerged}
        style={elementStyle}
        ref={internalRef}
        tabIndex={disabled ? undefined : (tabIndex ?? 0)}
        data-testid={dataTestId}
        {...htmlProps}
        onKeyPress={onKeyPress}
      >
        <input
          tabIndex={-1}
          type="checkbox"
          checked={typeof onChange === "function" ? !!value : value === true}
          onChange={(e) => {
            if (typeof onChange === "function") {
              onChange(e.target.checked);
            }
          }}
        />
        <div className={style.checkButton}>
          <span className={style.checkMark}>
            {value === "partial" ? (
              <Icon icon="feather:minus" />
            ) : (
              <Icon icon="feather:check" />
            )}
          </span>
        </div>
        <span className={style.text}>{children}</span>
      </label>
    );
  },
);

Checkbox.displayName = "Checkbox";

export default Checkbox;
